// Turning finger movement into drawings, without breaking the chart.
//
// The hard constraint: the same gestures that draw are the gestures that pan
// and zoom. Get this wrong in either direction and the page is unusable --
// either every attempt to scroll leaves a line behind, or a line can never be
// grabbed. The rules that resolve it:
//
//   * a pointerdown that hits nothing, with no tool armed, is a PAN. We do not
//     touch it.
//   * CREATING is tap-to-place, never drag-to-draw. TradingView abandoned
//     drag-to-create on touch for a reason: the finger covers the anchor it is
//     placing, and every mis-hit becomes a line the user did not want. Tap
//     places one anchor; a two-anchor shape waits for the second tap. Drag
//     therefore always means pan or move-an-existing-line, which makes the
//     gesture vocabulary learnable in one attempt.
//   * a second finger always means pinch. If one arrives mid-drag we cancel
//     and put the anchors back where they were -- they meant to zoom.
//   * chart scroll/scale options are SAVED before being disabled and restored
//     from that saved copy, never from a literal. This app sets a non-default
//     handleScale.axisPressedMouseMove; restoring `true` would silently kill
//     price-axis dragging for the rest of the session.

import { snapPrice, toleranceFor } from "./drawing-geometry.js";

const LONG_PRESS_MS = 400;

export class DrawingController {
  /**
   * @param {object} opts
   *   chart, series, layer, element  -- the chart and its DOM host
   *   onChange(drawing, phase)       -- 'created' | 'moved' | 'done'
   *   onSelect(drawing|null)
   *   onLongPress(drawing, x, y)
   *   snapTargets()                  -- [{price,label}] the magnet may lock to
   */
  constructor(opts) {
    this.chart = opts.chart;
    this.series = opts.series;
    this.layer = opts.layer;
    this.el = opts.element;
    this.onChange = opts.onChange || (() => {});
    this.onSelect = opts.onSelect || (() => {});
    this.onLongPress = opts.onLongPress || (() => {});
    this.snapTargets = opts.snapTargets || (() => []);
    // Price alerts, draggable like MT5's order lines. They are not drawings
    // -- they render as price lines -- so they get their own hit-test by
    // price row and their own drag lifecycle.
    this.alertTargets = opts.alertTargets || (() => []);
    this.onAlertDrag = opts.onAlertDrag || (() => {});
    this._alertDrag = null;

    this.armedTool = null;      // 'hline' | 'trendline' | 'ray' | 'rect'
    // Stamped onto each drawing at creation, so the layer can label it later.
    this.timeframe = opts.timeframe || null;
    this.magnet = true;
    this._drag = null;
    this._placing = null;
    this._savedOptions = null;
    this._frame = null;
    this._pointers = new Set();
    this._longPressTimer = null;

    this._onDown = this._onDown.bind(this);
    this._onMove = this._onMove.bind(this);
    this._onUp = this._onUp.bind(this);
    this._onCancel = this._onCancel.bind(this);

    this.el.addEventListener("pointerdown", this._onDown);
    this.el.addEventListener("pointermove", this._onMove);
    this.el.addEventListener("pointerup", this._onUp);
    this.el.addEventListener("pointercancel", this._onCancel);
  }

  destroy() {
    this.el.removeEventListener("pointerdown", this._onDown);
    this.el.removeEventListener("pointermove", this._onMove);
    this.el.removeEventListener("pointerup", this._onUp);
    this.el.removeEventListener("pointercancel", this._onCancel);
    this._restoreChart();
    clearTimeout(this._longPressTimer);
    if (this._frame) cancelAnimationFrame(this._frame);
    // touch-action:none survived the controller. A rebuild while a tool was
    // armed left the chart element permanently refusing browser gestures, with
    // nothing listening to interpret them -- the page could not be scrolled
    // past the chart and nothing could be drawn either.
    this.el.style.touchAction = "";
    // A rebuild mid-placement (chart type flip, timeframe switch) must tell
    // React the half-built shape is dead, or a zero-length ghost stays in the
    // drawings list and the count badge lies by one.
    if (this._placing) this.onChange(this._placing, "cancelled");
    this._placing = null;
    this._drag = null;
  }

  arm(tool) {
    // Abandon a half-placed shape on ANY change of tool, not only on
    // disarming. Tapping trendline, placing one anchor, then tapping Zone left
    // the pending trendline alive: the next tap finished it, and the user got
    // a shape they had already navigated away from.
    if (this._placing && tool !== this.armedTool) {
      this.onChange(this._placing, "cancelled");
      this._placing = null;
      this.layer.repaint();
    }
    this.armedTool = tool || null;
    if (!this.armedTool && this._placing) {
      // Disarming mid-placement abandons the half-built shape rather than
      // leaving a one-anchor ghost on the chart.
      this.onChange(this._placing, "cancelled");
      this._placing = null;
      this.layer.repaint();
    }
    // touch-action is disabled only WHILE armed. Making it permanent would
    // break scrolling the page past the chart, which is most of the app.
    this.el.style.touchAction = this.armedTool ? "none" : "";
  }

  setMagnet(on) { this.magnet = !!on; }

  _local(event) {
    const rect = this.el.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  // Pixels back to an anchor, with the magnet applied.
  _anchorAt(x, y) {
    const price = this.series.coordinateToPrice(y);
    const time = this.chart.timeScale().coordinateToTime(x);
    const logical = this.chart.timeScale().coordinateToLogical(x);
    if (price === null || price === undefined) return null;
    let p = price;
    let snappedTo = null;
    if (this.magnet) {
      const snapped = snapPrice(price, y, this.snapTargets(),
                                (value) => this.series.priceToCoordinate(value), 8);
      p = snapped.price;
      snappedTo = snapped.snappedTo;
    }
    // `l` is carried in memory for the current session only. It is stripped
    // before the anchor is sent to the server -- a logical index goes stale
    // the moment this app prepends history on scroll-back, and a persisted one
    // would put the drawing hundreds of bars from where it was placed.
    return { t: time, p, l: logical, snappedTo };
  }

  _freezeChart() {
    if (this._savedOptions) return;
    // Save first. Restoring from a literal would destroy this app's
    // non-default axisPressedMouseMove settings.
    this._savedOptions = {
      handleScroll: this.chart.options().handleScroll,
      handleScale: this.chart.options().handleScale,
    };
    this.chart.applyOptions({ handleScroll: false, handleScale: false });
  }

  _restoreChart() {
    if (!this._savedOptions) return;
    this.chart.applyOptions(this._savedOptions);
    this._savedOptions = null;
  }

  _onDown(event) {
    this._pointers.add(event.pointerId);

    // A second finger is a pinch, always. Cancel any drag and put it back.
    if (this._pointers.size > 1) {
      this._cancelDrag();
      return;
    }
    if (!event.isPrimary) return;

    const { x, y } = this._local(event);

    if (this.armedTool) {
      const anchor = this._anchorAt(x, y);
      if (!anchor) return;
      event.preventDefault();

      // Second tap of a two-anchor shape: finish it.
      if (this._placing) {
        const drawing = this._placing;
        drawing.anchors[1] = anchor;
        if (drawing.kind === "trade" && drawing.anchors.length === 2) {
          // The two taps drew entry -> stop; the target seeds itself on the
          // other side at the app's own minimum R:R (1.5), and all three
          // anchors stay individually draggable from their handles.
          const [entry, stop] = drawing.anchors;
          drawing.anchors.push({ ...entry, p: entry.p + (entry.p - stop.p) * 1.5 });
        }
        if (drawing.kind === "channel" && drawing.anchors.length === 2) {
          // The rail pair is drawn; the offset anchor seeds parallel below
          // (or above, for a falling swing) and drags from its handle.
          const [a1, a2] = drawing.anchors;
          const span = Math.abs(a2.p - a1.p) || Math.abs(a1.p) * 0.004;
          drawing.anchors.push({ ...a1, p: a1.p - span * 0.6 });
        }
        delete drawing.pending;
        this._placing = null;
        this.arm(null);
        this.layer.select(drawing.id);
        this.onSelect(drawing);
        this.onChange(drawing, "done");
        return;
      }

      const kind = this.armedTool;
      const oneTap = kind === "hline" || kind === "vline";
      const drawing = {
        id: `d-${Date.now()}-${Math.floor(Math.random() * 1e6)}`,
        kind,
        anchors: oneTap ? [anchor] : [anchor, { ...anchor }],
        style: {},
      };
      if (oneTap) {
        // One anchor is the whole shape: one tap finishes it.
        drawing.timeframe = this.timeframe || null;
        this.arm(null);
        this.layer.select(drawing.id);
        this.onSelect(drawing);
        this.onChange(drawing, "created");
        this.onChange(drawing, "done");
        return;
      }
      drawing.pending = true;
      drawing.timeframe = this.timeframe || null;
      this._placing = drawing;
      this.onChange(drawing, "created");
      return;
    }

    const hit = this.layer.hitTestLocal(x, y, event.pointerType);
    if (!hit) {
      // An alert line under the finger drags even though it is not a
      // drawing. Drawings win ties -- they were placed deliberately on top.
      const alert = this._alertAt(y, event.pointerType);
      if (alert) {
        event.preventDefault();
        this.el.setPointerCapture(event.pointerId);
        this._freezeChart();
        this._alertDrag = { id: alert.id, price: alert.price,
                           original: alert.price, moved: false };
        this.onAlertDrag(alert.id, "grab", alert.price);
        return;
      }
      // Nothing under the finger and nothing armed: this is a pan. Deselect,
      // and get out of the way.
      if (this.layer._selectedId) {
        this.layer.select(null);
        this.onSelect(null);
      }
      return;
    }

    event.preventDefault();
    this.el.setPointerCapture(event.pointerId);
    this._freezeChart();
    this.layer.select(hit.id);
    this.onSelect(hit.drawing);
    this._drag = {
      mode: hit.part === "handle" ? "handle" : "body",
      drawing: hit.drawing,
      index: hit.index,
      startX: x,
      startY: y,
      // A copy, so a cancelled drag can be undone exactly.
      original: JSON.parse(JSON.stringify(hit.drawing.anchors)),
      moved: false,
    };

    this._longPressTimer = setTimeout(() => {
      if (this._drag && !this._drag.moved) {
        this.onLongPress(hit.drawing, x, y);
        this._cancelDrag();
      }
    }, LONG_PRESS_MS);
  }

  // Which alert's line is under this y, if any. Nearest within the touch
  // tolerance wins -- same floor the drawings use.
  _alertAt(y, pointerType) {
    const tol = toleranceFor(pointerType).line;
    let best = null;
    for (const alert of this.alertTargets()) {
      if (alert == null || alert.price == null) continue;
      let py = null;
      try { py = this.series.priceToCoordinate(alert.price); } catch (e) { continue; }
      if (py === null || py === undefined) continue;
      const d = Math.abs(y - py);
      if (d <= tol && (!best || d < best.d)) best = { d, id: alert.id, price: alert.price };
    }
    return best;
  }

  _onMove(event) {
    if (this._alertDrag && event.isPrimary) {
      const { y } = this._local(event);
      let price = null;
      try { price = this.series.coordinateToPrice(y); } catch (e) { /* off scale */ }
      if (price != null) {
        this._alertDrag.price = price;
        this._alertDrag.moved = true;
        this.onAlertDrag(this._alertDrag.id, "move", price);
      }
      return;
    }
    if (this._placing && event.isPrimary) {
      const { x, y } = this._local(event);
      const anchor = this._anchorAt(x, y);
      if (anchor) {
        this._placing.anchors[1] = anchor;
        this.layer.repaint();
      }
      return;
    }
    if (!this._drag || !event.isPrimary) return;
    const { x, y } = this._local(event);
    if (!this._drag.moved) {
      const start = this._drag.startX == null ? null : Math.hypot(x - this._drag.startX, y - this._drag.startY);
      if (start === null || start > 4) {
        this._drag.moved = true;
        clearTimeout(this._longPressTimer);
      }
    }
    // Coalesce into one frame: a phone fires pointermove far faster than the
    // chart can repaint, and painting per event is how a drag turns to syrup.
    this._pending = { x, y };
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      this._apply(this._pending.x, this._pending.y);
    });
  }

  _apply(x, y) {
    const drag = this._drag;
    if (!drag) return;
    const anchor = this._anchorAt(x, y);
    if (!anchor) return;
    const drawing = drag.drawing;

    if (drag.mode === "handle") {
      drawing.anchors[drag.index] = anchor;
    } else {
      // Moving the whole shape. Always coordinate -> value, converted once per
      // frame from the CURRENT pointer position. Accumulating price deltas
      // drifts as soon as the user zooms mid-drag.
      const dy = y - drag.startY;
      const dx = x - drag.startX;
      drawing.anchors = drag.original.map((a) => {
        const y0 = this.series.priceToCoordinate(a.p);
        if (y0 === null) return a;
        const price = this.series.coordinateToPrice(y0 + dy);
        const moved = { ...a, p: price === null ? a.p : price };
        if (drawing.kind !== "hline") {
          // An anchor placed right of the last bar has NO time, and
          // timeToCoordinate(null) is an assertion failure in the dev build --
          // it threw on every frame of the drag, so the shape never moved and
          // the chart stayed frozen with scrolling disabled. Fall back to the
          // logical index, which is what such an anchor is positioned by.
          const scale = this.chart.timeScale();
          let x0 = null;
          try {
            x0 = a.t == null ? null : scale.timeToCoordinate(a.t);
          } catch (error) {
            x0 = null;
          }
          if ((x0 === null || x0 === undefined) && a.l != null) {
            x0 = scale.logicalToCoordinate(a.l);
          }
          if (x0 !== null && x0 !== undefined) {
            const time = scale.coordinateToTime(x0 + dx);
            const logical = scale.coordinateToLogical(x0 + dx);
            // Both are updated together. Moving only `t` left the logical
            // index describing where the anchor USED to be, which sheared the
            // shape whenever the drag reached the right margin -- exactly
            // where `t` goes null.
            moved.t = time == null ? null : time;
            if (logical != null) moved.l = logical;
          }
        }
        return moved;
      });
    }
    this.layer.repaint();
    this.onChange(drawing, "moved");
  }

  _cancelDrag() {
    clearTimeout(this._longPressTimer);
    const drag = this._drag;
    if (drag && drag.original) drag.drawing.anchors = drag.original;
    if (drag && drag.mode === "create") this.onChange(drag.drawing, "cancelled");
    this._drag = null;
    if (this._alertDrag) {
      // The pinch (or teardown) interrupts the slide: the line goes back
      // where the alert actually is.
      this.onAlertDrag(this._alertDrag.id, "cancel", this._alertDrag.original);
      this._alertDrag = null;
    }
    this._restoreChart();
    this.layer.repaint();
  }

  _onUp(event) {
    this._pointers.delete(event.pointerId);
    clearTimeout(this._longPressTimer);
    if (this._alertDrag && event.isPrimary) {
      const alertDrag = this._alertDrag;
      this._alertDrag = null;
      this._restoreChart();
      // A grab that never moved is a tap, not an edit -- put it back.
      this.onAlertDrag(alertDrag.id,
                       alertDrag.moved ? "done" : "cancel",
                       alertDrag.moved ? alertDrag.price : alertDrag.original);
      return;
    }
    const drag = this._drag;
    this._drag = null;
    this._restoreChart();
    if (!drag) return;

    this.onChange(drag.drawing, "done");
  }

  _onCancel(event) {
    this._pointers.delete(event.pointerId);
    this._cancelDrag();
  }
}
